import type { ReactNode } from 'react'
import type { KeyInsightQuestion } from '@/types'
import { tokenizeHashtags } from '@/lib/hashtags'
import { formatDuration } from './formatters'

interface RecordingMetadataProps {
  recordingId: string
  recordingName: string
  isDraft: boolean
  isTextNote: boolean
  noteBody: string | null | undefined
  isLongNote: boolean
  onOpenNote: () => void
  durationMs: number
  hasPlayableAudio: boolean
  displayDate: string
  blobSource: Blob | null | undefined
  playbackSupported: boolean
  kiqs: KeyInsightQuestion[]
  hasOtherKiqOnly: boolean
  insightTags?: string[]
  statusBadge: ReactNode
}

export function RecordingMetadata({
  recordingId,
  recordingName,
  isDraft,
  isTextNote,
  noteBody,
  isLongNote,
  onOpenNote,
  durationMs,
  hasPlayableAudio,
  displayDate,
  blobSource,
  playbackSupported,
  kiqs,
  hasOtherKiqOnly,
  insightTags = [],
  statusBadge,
}: RecordingMetadataProps) {
  return (
    <div className="flex-1 min-w-0">
      <h3 id={`recording-title-${recordingId}`} className="text-white font-medium truncate">
        {recordingName}
        {!isDraft && isTextNote && (
          <span className="ml-2 text-xs text-gray-400 font-normal" aria-label="Text note">
            (Text Note)
          </span>
        )}
      </h3>
      <p className="text-gray-400 text-sm mt-1">
        {hasPlayableAudio && durationMs ? `${formatDuration(durationMs)} • ` : ''}
        {displayDate}
      </p>

      <div className="mt-2" aria-live="polite">
        {statusBadge}
      </div>

      {blobSource && !playbackSupported && (
        <p className="mt-2 text-xs text-yellow-300">
          This recording format is not supported here. Replay it in a browser that supports the
          original format or capture a new recording.
        </p>
      )}

      {isTextNote && noteBody && (
        <div className="mt-2">
          <p className="text-gray-300 text-sm line-clamp-2">
            {tokenizeHashtags(noteBody).map((segment, index) =>
              segment.isHashtag ? (
                <span key={index} className="font-medium text-neon-cyan">
                  {segment.text}
                </span>
              ) : (
                <span key={index}>{segment.text}</span>
              ),
            )}
          </p>
          <button
            onClick={onOpenNote}
            className="mt-2 text-xs text-neon-cyan hover:text-neon-cyan/80 underline focus:outline-none focus:ring-2 focus:ring-neon-cyan rounded"
            aria-label="View full note"
          >
            {isLongNote ? 'View details' : 'View note'}
          </button>
        </div>
      )}

      {(kiqs.length > 0 || hasOtherKiqOnly) && (
        <div className="mt-3 flex flex-wrap gap-2" role="list" aria-label="Key insight questions">
          {hasOtherKiqOnly && (
            <span
              className="inline-flex items-center rounded-2xl bg-neon-cyan/20 px-3 py-1 text-xs font-medium text-neon-cyan border border-neon-cyan/30"
              role="listitem"
            >
              Other
            </span>
          )}
          {kiqs.map((kiq) => (
            <span
              key={kiq.id}
              className="inline-flex items-center rounded-2xl bg-neon-cyan/20 px-3 py-1 text-xs font-medium text-neon-cyan border border-neon-cyan/30"
              role="listitem"
            >
              {kiq.prompt}
            </span>
          ))}
        </div>
      )}

      {insightTags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" role="list" aria-label="Insight types">
          {insightTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-2xl bg-navy px-3 py-1 text-xs font-medium text-[#7cb0e8] border border-[#7cb0e8]/40"
              role="listitem"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
